const STORAGE_KEY = 'kirin:lastVisitedPath';

/**
 * Validates pathname (before `?`) for open-redirect hazards. Query-string **values**
 * are not scanned for `https://…` etc.: `window.location.assign` with a path starting
 * with `/` stays same-origin; query params are data, not a navigation target.
 */
function isSafeRelativeAppPath(path: string): boolean {
  if (path.length === 0) {
    return false;
  }
  if (!path.startsWith('/')) {
    return false;
  }
  if (path.startsWith('//')) {
    return false;
  }
  if (path.includes('\0')) {
    return false;
  }
  // Reject backslash: URL parsers may treat it like `/` (e.g. `/\host` → protocol-relative).
  if (path.includes('\\')) {
    return false;
  }
  const beforeQuery = path.split('?')[0] ?? path;
  if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(beforeQuery)) {
    return false;
  }
  return true;
}

/**
 * Returns a safe same-origin path+search for redirects, or null if invalid.
 */
export function sanitizeLastVisitedPath(path: string): string | null {
  const trimmed = path.trim();
  if (!isSafeRelativeAppPath(trimmed)) {
    return null;
  }
  return trimmed;
}

/**
 * Persist the latest in-app URL (pathname + search) for post–401 redirect.
 */
export function setLastVisitedPath(path: string): void {
  const safe = sanitizeLastVisitedPath(path);
  if (safe === null) {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, safe);
  } catch {
    // Quota, private mode, or disabled storage
  }
}

/**
 * Remove persisted last-visited path (call on logout so the next user in this tab
 * does not inherit the previous session's redirect target).
 */
export function clearLastVisitedPath(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Quota, private mode, or disabled storage
  }
}

/**
 * Read the last persisted path, or null if missing or invalid.
 */
export function getLastVisitedPath(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return sanitizeLastVisitedPath(raw);
  } catch {
    return null;
  }
}

/**
 * Path to use after clearing session on 401: stored last visit, else current location, else /.
 */
export function resolveRedirectPathAfterAuthFailure(): string {
  const fromStorage = getLastVisitedPath();
  const current = `${window.location.pathname}${window.location.search}`;
  const candidate = fromStorage ?? current;
  return sanitizeLastVisitedPath(candidate) ?? '/';
}

/**
 * Full-page navigation after session invalidation (401, refresh failure).
 * Keeps api-client and TokenManagerProvider aligned on post-auth-failure URL.
 */
export function assignLocationAfterAuthFailure(): void {
  window.location.assign(resolveRedirectPathAfterAuthFailure());
}
